/**
 * MonsterStateContext
 *
 * Shared monster state (my/opp monsters, selection) so all consumers see the same
 * data. Opponent `xed` / `guess` come from Redis via join payload + `update monster deduction`.
 *
 * Depends on GameStateContext (provider must be inside GameStateProvider).
 */

import React, {
    createContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useGameStateContext } from './useGameStateContext';
import useGameSocket from '../hooks/useGameSocket';
import { getMonsterById } from '../utils/constants';
import type {
    FrontendMonster,
    SelectedMonster,
    MonsterTypeId,
    MonsterName,
    PlayerDeductionsDocument,
} from '../types/monster.types';
import type { GameState, PlayerLetter } from '../types/game.types';
import type { MonsterMovedPayload } from '../types/socket.types';

function mergeDeductionSlot(
    prev: PlayerDeductionsDocument | null,
    roomCode: string,
    viewerLetter: PlayerLetter,
    opponentLetter: PlayerLetter,
    monsterId: number,
    xed: MonsterTypeId[],
    guess: MonsterTypeId | null
): PlayerDeductionsDocument {
    const key = `${opponentLetter}:${monsterId}`;
    const base: PlayerDeductionsDocument =
        prev ?? {
            roomCode,
            playerLetter: viewerLetter,
            byTargetMonster: {},
            lastUpdated: new Date().toISOString(),
        };

    return {
        ...base,
        roomCode: roomCode || base.roomCode,
        playerLetter: viewerLetter,
        byTargetMonster: {
            ...base.byTargetMonster,
            [key]: { xed: [...xed], guess },
        },
        lastUpdated: new Date().toISOString(),
    };
}

export interface MonsterStateContextValue {
    myMonsters: FrontendMonster[];
    oppMonsters: FrontendMonster[];
    selectedMonster: SelectedMonster | null;
    setSelectedMonster: (monster: SelectedMonster | null) => void;
    selectedPiece: FrontendMonster | null;
    setSelectedPiece: (piece: FrontendMonster | null) => void;
    getMonster: (monsterId: number, playerLetter: PlayerLetter) => FrontendMonster | null;
    getPiece: (monsterType: MonsterTypeId, playerLetter: PlayerLetter) => FrontendMonster | null;
    getMyMonster: (monsterId: number) => FrontendMonster | null;
    getOppMonster: (monsterId: number) => FrontendMonster | null;
    addMonster: (monster: FrontendMonster, playerLetter: PlayerLetter) => void;
    updateMonster: (monsterId: number, updates: Partial<FrontendMonster>, playerLetter: PlayerLetter) => void;
    removeMonster: (monsterId: number, playerLetter: PlayerLetter) => void;
    getAliveMonsters: (playerLetter: PlayerLetter) => FrontendMonster[];
    getDeadMonsters: (playerLetter: PlayerLetter) => FrontendMonster[];
    getMyAliveMonsters: () => FrontendMonster[];
    getOppAliveMonsters: () => FrontendMonster[];
    getUnplacedMonsters: (playerLetter: PlayerLetter) => FrontendMonster[];
    getMyUnplacedMonsters: () => FrontendMonster[];
    getOppUnplacedMonsters: () => FrontendMonster[];
}

export const MonsterStateContext = createContext<MonsterStateContextValue | null>(null);

export interface MonsterStateProviderProps {
    children: React.ReactNode;
    /**
     * Hydration from `join room` / reconnect. `undefined` = do not overwrite local doc
     * (initial load or lobby). `null` or object = replace doc when game is present.
     */
    initialDeductionsDocument?: PlayerDeductionsDocument | null;
}

function mapBackendMonsters(gameState: GameState, playerLetter: PlayerLetter): FrontendMonster[] {
    const backendMonsters = gameState.players?.[playerLetter]?.monsters ?? [];
    return backendMonsters
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((m) => {
            const monsterTypeId = m.type as MonsterTypeId;
            const monsterDef = getMonsterById(monsterTypeId);
            return {
                id: m.id,
                type: monsterTypeId,
                name: (monsterDef?.name as MonsterName) || null,
                player: playerLetter,
                position: m.position,
                alive: m.alive,
                guess: null,
                xed: [],
                movement: monsterDef?.movement || [],
                imageUrl: null,
            };
        });
}

export const MonsterStateProvider: React.FC<MonsterStateProviderProps> = ({
    children,
    initialDeductionsDocument,
}) => {
    const { myPlayerLetter, gameState, phase } = useGameStateContext();
    const { onMonsterMoved, updateMonsterDeduction, getPlayerDeductions } = useGameSocket();

    const [deductionsDoc, setDeductionsDoc] = useState<PlayerDeductionsDocument | null>(null);
    const [selectedMonster, setSelectedMonster] = useState<SelectedMonster | null>(null);
    const [selectedPiece, setSelectedPiece] = useState<FrontendMonster | null>(null);

    /** `parent` = GamePage join/reconnect supplied doc; `fetched` = loaded via socket (lobby → game). */
    const deductionsHydrationRef = useRef<'pending' | 'parent' | 'fetched'>('pending');

    useEffect(() => {
        if (initialDeductionsDocument === undefined) return;
        setDeductionsDoc(initialDeductionsDocument);
        deductionsHydrationRef.current = 'parent';
    }, [initialDeductionsDocument]);

    useEffect(() => {
        if (!gameState || !myPlayerLetter) return;
        if (deductionsHydrationRef.current !== 'pending') return;

        deductionsHydrationRef.current = 'fetched';
        getPlayerDeductions((res) => {
            if (res.success) {
                setDeductionsDoc(res.playerDeductions ?? null);
            }
        });
    }, [gameState, myPlayerLetter, getPlayerDeductions]);

    useEffect(() => {
        setSelectedMonster(null);
        setSelectedPiece(null);
    }, [phase]);

    const opponentLetter: PlayerLetter | null =
        myPlayerLetter === 'A' ? 'B' : myPlayerLetter === 'B' ? 'A' : null;

    const myMonsters = useMemo(() => {
        if (!gameState || !myPlayerLetter) return [];
        return mapBackendMonsters(gameState, myPlayerLetter);
    }, [gameState, myPlayerLetter]);

    const oppMonsters = useMemo(() => {
        if (!gameState || !myPlayerLetter || !opponentLetter) return [];
        const base = mapBackendMonsters(gameState, opponentLetter);
        const map = deductionsDoc?.byTargetMonster;
        if (!map) {
            return base;
        }
        return base.map((m) => {
            const key = `${opponentLetter}:${m.id}`;
            const slot = map[key];
            if (!slot) {
                return { ...m, xed: [] as MonsterTypeId[], guess: null };
            }
            return {
                ...m,
                xed: slot.xed as MonsterTypeId[],
                guess: slot.guess as MonsterTypeId | null,
            };
        });
    }, [gameState, myPlayerLetter, opponentLetter, deductionsDoc]);

    const addMonster = useCallback((_monster: FrontendMonster, _playerLetter: PlayerLetter) => {
        /* Game state is authoritative; list derives from gameState */
    }, []);

    const updateMonster = useCallback(
        (monsterId: number, updates: Partial<FrontendMonster>, playerLetter: PlayerLetter) => {
            if (!myPlayerLetter || !gameState?.roomCode) return;

            if (playerLetter === myPlayerLetter) {
                return;
            }

            if (updates.xed !== undefined || updates.guess !== undefined) {
                const xed = (updates.xed ?? []) as MonsterTypeId[];
                const guess = updates.guess !== undefined ? updates.guess : null;

                setDeductionsDoc((prev) =>
                    mergeDeductionSlot(
                        prev,
                        gameState.roomCode,
                        myPlayerLetter,
                        playerLetter,
                        monsterId,
                        xed,
                        guess
                    )
                );

                updateMonsterDeduction(
                    {
                        opponentLetter: playerLetter,
                        monsterId,
                        xed: [...xed],
                        guess,
                    },
                    (res) => {
                        if (!res.success && res.error) {
                            console.warn('[MonsterState] Deduction save failed:', res.error);
                        }
                    }
                );
            }
        },
        [myPlayerLetter, gameState?.roomCode, updateMonsterDeduction]
    );

    const removeMonster = useCallback((_monsterId: number, _playerLetter: PlayerLetter) => {
        /* Game state is authoritative */
    }, []);

    const getPiece = useCallback(
        (monsterType: MonsterTypeId, playerLetter: PlayerLetter): FrontendMonster | null => {
            const monsters = playerLetter === myPlayerLetter ? myMonsters : oppMonsters;
            return monsters.find(m => m.type === monsterType) || null;
        },
        [myMonsters, oppMonsters, myPlayerLetter]
    );

    const getMonster = useCallback(
        (monsterId: number, playerLetter: PlayerLetter): FrontendMonster | null => {
            const monsters = playerLetter === myPlayerLetter ? myMonsters : oppMonsters;
            return monsters.find(m => m.id === monsterId) || null;
        },
        [myMonsters, oppMonsters, myPlayerLetter]
    );

    useEffect(() => {
        setSelectedPiece((prev) => {
            if (!prev) return null;
            const fresh = getMonster(prev.id, prev.player);
            return fresh ?? null;
        });
    }, [getMonster]);

    useEffect(() => {
        return onMonsterMoved((payload: MonsterMovedPayload) => {
            const { toRow, toCol } = payload;
            setSelectedPiece((prev) => {
                if (!prev?.position) return prev;
                const pos = prev.position;
                if (pos.row === toRow && pos.col === toCol) {
                    return null;
                }
                return prev;
            });
        });
    }, [onMonsterMoved]);

    const getMyMonster = useCallback(
        (monsterId: number) => myMonsters.find(m => m.id === monsterId) || null,
        [myMonsters]
    );

    const getOppMonster = useCallback(
        (monsterId: number) => oppMonsters.find(m => m.id === monsterId) || null,
        [oppMonsters]
    );

    const getAliveMonsters = useCallback(
        (playerLetter: PlayerLetter) => {
            const monsters = playerLetter === myPlayerLetter ? myMonsters : oppMonsters;
            return monsters.filter(m => m.alive);
        },
        [myMonsters, oppMonsters, myPlayerLetter]
    );

    const getDeadMonsters = useCallback(
        (playerLetter: PlayerLetter) => {
            const monsters = playerLetter === myPlayerLetter ? myMonsters : oppMonsters;
            return monsters.filter(m => !m.alive);
        },
        [myMonsters, oppMonsters, myPlayerLetter]
    );

    const getMyAliveMonsters = useCallback(
        () => myMonsters.filter(m => m.alive),
        [myMonsters]
    );

    const getOppAliveMonsters = useCallback(
        () => oppMonsters.filter(m => m.alive),
        [oppMonsters]
    );

    const getUnplacedMonsters = useCallback(
        (playerLetter: PlayerLetter) => {
            const monsters = playerLetter === myPlayerLetter ? myMonsters : oppMonsters;
            return monsters.filter(
                m => !m.position || (m.position.row === -1 && m.position.col === -1)
            );
        },
        [myMonsters, oppMonsters, myPlayerLetter]
    );

    const getMyUnplacedMonsters = useCallback(
        () =>
            myMonsters.filter(
                m => !m.position || (m.position.row === -1 && m.position.col === -1)
            ),
        [myMonsters]
    );

    const getOppUnplacedMonsters = useCallback(
        () =>
            oppMonsters.filter(
                m => !m.position || (m.position.row === -1 && m.position.col === -1)
            ),
        [oppMonsters]
    );

    const value: MonsterStateContextValue = {
        myMonsters,
        oppMonsters,
        selectedMonster,
        setSelectedMonster,
        selectedPiece,
        setSelectedPiece,
        getMonster,
        getPiece,
        getMyMonster,
        getOppMonster,
        addMonster,
        updateMonster,
        removeMonster,
        getAliveMonsters,
        getDeadMonsters,
        getMyAliveMonsters,
        getOppAliveMonsters,
        getUnplacedMonsters,
        getMyUnplacedMonsters,
        getOppUnplacedMonsters,
    };

    return (
        <MonsterStateContext.Provider value={value}>
            {children}
        </MonsterStateContext.Provider>
    );
};
